import { Agent } from "app-types/agent";

/**
 * Service for managing agent context persistence and cleanup
 */
export class AgentContextService {
  private static readonly STORAGE_KEY = "agent-context";
  private static readonly THREAD_MAPPING_KEY = "agent-thread-mapping";

  /**
   * Save agent context to browser storage
   */
  static saveAgentContext(agent: Agent, threadId?: string): void {
    try {
      const context = {
        agentId: agent.id,
        agentName: agent.name,
        threadId,
        timestamp: Date.now(),
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(context));

      // Also save thread mapping
      if (threadId) {
        this.saveAgentThreadMapping(agent.id, threadId);
      }
    } catch (error) {
      console.warn("Failed to save agent context:", error);
    }
  }

  /**
   * Load agent context from browser storage
   */
  static loadAgentContext(): {
    agentId: string;
    agentName: string;
    threadId?: string;
    timestamp: number;
  } | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const context = JSON.parse(stored);

      // Check if context is not too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - context.timestamp > maxAge) {
        this.clearAgentContext();
        return null;
      }

      return context;
    } catch (error) {
      console.warn("Failed to load agent context:", error);
      return null;
    }
  }

  /**
   * Clear agent context from browser storage
   */
  static clearAgentContext(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear agent context:", error);
    }
  }

  /**
   * Save agent-thread mapping
   */
  static saveAgentThreadMapping(agentId: string, threadId: string): void {
    try {
      const stored = localStorage.getItem(this.THREAD_MAPPING_KEY);
      const mapping = stored ? JSON.parse(stored) : {};

      mapping[agentId] = {
        threadId,
        timestamp: Date.now(),
      };

      localStorage.setItem(this.THREAD_MAPPING_KEY, JSON.stringify(mapping));
    } catch (error) {
      console.warn("Failed to save agent thread mapping:", error);
    }
  }

  /**
   * Load agent-thread mapping
   */
  static loadAgentThreadMapping(): Record<
    string,
    { threadId: string; timestamp: number }
  > {
    try {
      const stored = localStorage.getItem(this.THREAD_MAPPING_KEY);
      if (!stored) return {};

      const mapping = JSON.parse(stored);

      // Clean up old mappings (older than 7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      const now = Date.now();

      Object.keys(mapping).forEach((agentId) => {
        if (now - mapping[agentId].timestamp > maxAge) {
          delete mapping[agentId];
        }
      });

      // Save cleaned mapping back
      localStorage.setItem(this.THREAD_MAPPING_KEY, JSON.stringify(mapping));

      return mapping;
    } catch (error) {
      console.warn("Failed to load agent thread mapping:", error);
      return {};
    }
  }

  /**
   * Get thread ID for specific agent
   */
  static getAgentThreadId(agentId: string): string | null {
    const mapping = this.loadAgentThreadMapping();
    return mapping[agentId]?.threadId || null;
  }

  /**
   * Remove agent from thread mapping
   */
  static removeAgentThreadMapping(agentId: string): void {
    try {
      const stored = localStorage.getItem(this.THREAD_MAPPING_KEY);
      if (!stored) return;

      const mapping = JSON.parse(stored);
      delete mapping[agentId];

      localStorage.setItem(this.THREAD_MAPPING_KEY, JSON.stringify(mapping));
    } catch (error) {
      console.warn("Failed to remove agent thread mapping:", error);
    }
  }

  /**
   * Clear all agent thread mappings
   */
  static clearAllAgentThreadMappings(): void {
    try {
      localStorage.removeItem(this.THREAD_MAPPING_KEY);
    } catch (error) {
      console.warn("Failed to clear agent thread mappings:", error);
    }
  }

  /**
   * Handle browser navigation state
   */
  static updateBrowserHistory(agent: Agent | null, threadId?: string): void {
    try {
      const state = {
        agentId: agent?.id || null,
        agentName: agent?.name || null,
        threadId: threadId || null,
        timestamp: Date.now(),
      };

      // Update browser history state without changing URL
      window.history.replaceState(state, "", window.location.href);
    } catch (error) {
      console.warn("Failed to update browser history:", error);
    }
  }

  /**
   * Get browser history state
   */
  static getBrowserHistoryState(): {
    agentId: string | null;
    agentName: string | null;
    threadId: string | null;
    timestamp: number;
  } | null {
    try {
      return window.history.state || null;
    } catch (error) {
      console.warn("Failed to get browser history state:", error);
      return null;
    }
  }

  /**
   * Cleanup when switching contexts
   */
  static cleanupContext(previousAgent?: Agent): void {
    try {
      // Clear any temporary data
      if (previousAgent) {
        // Save the last known state for the previous agent
        const context = this.loadAgentContext();
        if (context && context.agentId === previousAgent.id) {
          // Context is still valid, keep it
          return;
        }
      }

      // Clear context if switching away from agents
      this.clearAgentContext();
    } catch (error) {
      console.warn("Failed to cleanup context:", error);
    }
  }

  /**
   * Initialize context from URL and storage
   */
  static initializeContext(pathname: string): {
    shouldRestoreAgent: boolean;
    agentId?: string;
    threadId?: string;
  } {
    try {
      // First check URL for agent context
      const urlMatch = pathname.match(/^\/agent\/([^\/]+)(?:\/([^\/]+))?$/);
      if (urlMatch) {
        return {
          shouldRestoreAgent: true,
          agentId: urlMatch[1],
          threadId: urlMatch[2],
        };
      }

      // Check stored context for main chat
      const storedContext = this.loadAgentContext();
      if (storedContext && pathname === "/") {
        return {
          shouldRestoreAgent: true,
          agentId: storedContext.agentId,
          threadId: storedContext.threadId,
        };
      }

      return { shouldRestoreAgent: false };
    } catch (error) {
      console.warn("Failed to initialize context:", error);
      return { shouldRestoreAgent: false };
    }
  }
}
